import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logoImage from "../assets/LU-2026--Main-Revised.png";

const LU_YELLOW = "#fffc00";
const LU_RED    = "#FF0000";

const BiometricIcon = ({ type, color = LU_RED, size = 24 }) => {
  const props = { width: size, height: size, stroke: color, strokeWidth: 2, fill: "none" };
  switch (type) {
    case "face": return (
      <svg viewBox="0 0 24 24" {...props}>
        <path d="M7 3H5a2 2 0 0 0-2 2v2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
        <path d="M9 10a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM16 10a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM12 11v3m-2 2s1.5 2 4 2" />
      </svg>
    );
    case "voice": return (
      <svg viewBox="0 0 24 24" {...props}>
        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="22" />
      </svg>
    );
    case "biometric": return (
      <svg viewBox="0 0 24 24" {...props}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
    default: return null;
  }
};

const BiometricScanner = ({ mode, onComplete }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(onComplete, 500);
          return 100;
        }
        return prev + 1;
      });
    }, 40);
    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <div style={{ position: "relative", width: "240px", height: "240px", margin: "40px auto" }}>
      <style>{`
        @keyframes rotate { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes scan { 0%, 100% { top: 0%; } 50% { top: 100%; } }
      `}</style>
      
      <div style={{ position: "absolute", inset: 0, border: `2px solid ${LU_RED}11`, borderRadius: "50%" }} />
      <div style={{ 
        position: "absolute", inset: "-10px", border: `2px dashed ${LU_YELLOW}`, borderRadius: "50%",
        animation: "rotate 20s linear infinite"
      }} />

      <div style={{ 
        inset: "10px", position: "absolute", background: "#fff",
        borderRadius: "50%", border: `1px solid ${LU_RED}22`, overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 30px rgba(0,0,0,0.05)"
      }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "100%", background: "linear-gradient(180deg, transparent, rgba(237, 28, 36, 0.05), transparent)", zIndex: 1, pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: LU_RED, boxShadow: `0 0 15px ${LU_RED}44`, zIndex: 2, animation: "scan 3s ease-in-out infinite" }} />

        <BiometricIcon type={mode} size={80} />
      </div>

      <svg style={{ position: "absolute", inset: -15, width: "270px", height: "270px", transform: "rotate(-90deg)" }}>
        <circle 
          cx="135" cy="135" r="130" fill="none" stroke={LU_RED} strokeWidth="4"
          strokeDasharray="816" strokeDashoffset={816 - (816 * progress) / 100}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.1s linear" }}
        />
      </svg>
      
      <div style={{ position: "absolute", bottom: "-40px", left: 0, right: 0, textAlign: "center", fontSize: "12px", fontWeight: 800, color: LU_RED, letterSpacing: "2px" }}>
        {progress}% VERIFIED
      </div>
    </div>
  );
};

const AuthApp = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("select");
  const [mode, setMode] = useState(null);
  const [isLogin, setIsLogin] = useState(true);
  const [userData, setUserData] = useState(null);

  // Check if user is already authenticated from QR login
  useEffect(() => {
    const isAuthenticated = localStorage.getItem("Authenticated");
    const storedData = localStorage.getItem("data");
    
    if (isAuthenticated === 'true' && storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (parsedData?.user?.is_authenticated === true) {
          setUserData(parsedData);
          setStep("success");
        }
      } catch (error) {
        console.error("Error parsing stored data:", error);
      }
    }
  }, []);

  const handleStartScan = (selectedMode) => {
    setMode(selectedMode);
    setStep("scan");
  };

  return (
    <div style={{ backgroundColor: LU_YELLOW, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", fontFamily: "'Outfit', sans-serif" }}>
      <div style={{ 
        width: "100%", maxWidth: "460px", background: "#fff",
        borderRadius: "32px", padding: "48px", position: "relative",
        boxShadow: "0 20px 60px rgba(0,0,0,0.1)"
      }}>
        
        {/* Branding */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "40px", cursor: "pointer" }} onClick={() => navigate("/")}>
          <img src={logoImage} alt="Legitimately U" style={{ height: "40px", objectFit: "contain" }} />
        </div>

        {step === "select" && (
          <div style={{ animation: "fadeIn 0.5s ease" }}>
            <h1 style={{ fontSize: "1.8rem", fontWeight: 900, marginBottom: "12px", lineHeight: 1.1, color: "#000" }}>
              Your Identity.<br /><span style={{ color: LU_RED }}>Verified Intelligently.</span>
            </h1>
            <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "32px" }}>Sign in once to access the entire integrated ecosystem.</p>

            {/* Toggle Login/Signup */}
            <div style={{ display: "flex", background: "rgba(0,0,0,0.05)", borderRadius: "12px", padding: "4px", marginBottom: "28px" }}>
              <button onClick={() => setIsLogin(true)} style={{ flex: 1, padding: "10px", border: "none", borderRadius: "8px", background: isLogin ? "#fff" : "transparent", color: isLogin ? LU_RED : "#64748b", fontWeight: 700, cursor: "pointer", boxShadow: isLogin ? "0 2px 4px rgba(0,0,0,0.05)" : "none" }}>Login</button>
              <button onClick={() => setIsLogin(false)} style={{ flex: 1, padding: "10px", border: "none", borderRadius: "8px", background: !isLogin ? "#fff" : "transparent", color: !isLogin ? LU_RED : "#64748b", fontWeight: 700, cursor: "pointer", boxShadow: !isLogin ? "0 2px 4px rgba(0,0,0,0.05)" : "none" }}>Signup</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {[
                { id: "face", name: "Face Verification", desc: "AI spatial mesh recognition" },
                { id: "voice", name: "Voice Authentication", desc: "Encrypted acoustic signature" },
                { id: "biometric", name: "Hardware Biometrics", desc: "Trusted platform module scan" }
              ].map(opt => (
                <button 
                  key={opt.id}
                  onClick={() => handleStartScan(opt.id)}
                  style={{ 
                    display: "flex", alignItems: "center", gap: "16px", padding: "16px",
                    background: "#f8fafc", border: "1px solid #eee",
                    borderRadius: "16px", cursor: "pointer", textAlign: "left", transition: "all 0.2s"
                  }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = LU_RED; e.currentTarget.style.background = "#fff"; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor = "#eee"; e.currentTarget.style.background = "#f8fafc"; }}
                >
                  <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: LU_YELLOW, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <BiometricIcon type={opt.id} color={LU_RED} size={20} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{opt.name}</div>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>{opt.desc}</div>
                  </div>
                  <span style={{ color: LU_RED, fontWeight: 900 }}>→</span>
                </button>
              ))}
            </div>

            <div style={{ marginTop: "40px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "12px", background: "rgba(16, 185, 129, 0.05)", borderRadius: "12px", border: "1px solid rgba(16, 185, 129, 0.1)" }}>
              <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#10b981" }} />
              <span style={{ fontSize: "11px", fontWeight: 800, color: "#10b981", letterSpacing: "1px" }}>UNIVERSAL SECURITY ACTIVE</span>
            </div>
          </div>
        )}

        {step === "scan" && (
          <div style={{ textAlign: "center", animation: "fadeIn 0.5s ease" }}>
            <h2 style={{ fontSize: "1.5rem", fontWeight: 900, marginBottom: "8px", color: "#000" }}>Securing Identity...</h2>
            <BiometricScanner mode={mode} onComplete={() => setStep("success")} />
          </div>
        )}

        {step === "success" && (
          <div style={{ textAlign: "center", animation: "scaleUp 0.4s ease forwards" }}>
            <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: "rgba(16, 185, 129, 0.1)", border: `2px solid #10b981`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h2 style={{ fontSize: "1.75rem", fontWeight: 900, marginBottom: "8px", color: "#000" }}>
              {userData?.user?.fullName ? `Welcome Back, ${userData.user.fullName}!` : "Authentication Successful"}
            </h2>
            <p style={{ color: "#64748b", marginBottom: "12px", fontSize: "14px" }}>
              {userData?.user?.email || "Verification confirmed by LegitimelyU Global Auth."}
            </p>
            <p style={{ color: "#64748b", marginBottom: "40px", fontSize: "14px" }}>Your identity has been verified. You are now in a secure biometric session.</p>
            
            <button className="btn-primary" style={{ border: "none", background: LU_RED, color: "#fff", width: "100%", padding: "16px", borderRadius: "16px", fontWeight: 800, cursor: "pointer" }} onClick={() => navigate("/lu-dashboard")}>
              Complete Access
            </button>
            
            <div style={{ marginTop: "20px", fontSize: "11px", color: "#9ca3af" }}>
               Redirecting to Platform Node...
            </div>
          </div>
        )}

      </div>
      
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleUp { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
};

export default AuthApp;
